// src/utils/taxonomyUtils.js
// Industry Taxonomy Utilities for SavvaiEdge
// Provides org-aware taxonomy with fallback to system defaults

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';

/**
 * System default taxonomy used when organization hasn't defined custom taxonomy
 * This list covers common SMB verticals and can be extended
 */
export const DEFAULT_INDUSTRY_TAXONOMY = [
  'Manufacturing',
  'Healthcare',
  'Financial Services',
  'Professional Services',
  'Technology',
  'Retail & E-commerce',
  'Real Estate',
  'Construction',
  'Transportation & Logistics',
  'Hospitality & Food Service',
  'Energy & Utilities',
  'Education',
  'Nonprofit',
  'Media & Entertainment',
  'Agriculture',
  'Other'
];

/**
 * Fetches the industry taxonomy for an organization
 * Returns org-specific taxonomy if defined, otherwise returns system default
 * 
 * @param {Firestore} db - Firestore instance
 * @param {string} organizationId - The organization ID (can be null for independent FCFOs)
 * @returns {Promise<string[]>} Array of industry names
 */
export async function getIndustryTaxonomy(db, organizationId) {
  if (!organizationId || !db) {
    return DEFAULT_INDUSTRY_TAXONOMY;
  }

  try {
    const orgRef = doc(db, 'organizations', organizationId);
    const orgSnap = await getDoc(orgRef);

    if (orgSnap.exists()) {
      const orgData = orgSnap.data();
      // Use org taxonomy if it exists and has values
      if (orgData.industryTaxonomy && Array.isArray(orgData.industryTaxonomy) && orgData.industryTaxonomy.length > 0) {
        // Ensure "Other" is always available as last option
        const taxonomy = [...orgData.industryTaxonomy];
        if (!taxonomy.includes('Other')) {
          taxonomy.push('Other');
        }
        return taxonomy;
      }
    }
  } catch (error) {
    console.error('[taxonomyUtils] Error fetching organization taxonomy:', error);
  }

  // Fallback to default
  return DEFAULT_INDUSTRY_TAXONOMY;
}

/**
 * React hook for fetching industry taxonomy
 * Handles loading state and caches result
 * 
 * Usage:
 *   const { taxonomy, loading, error } = useIndustryTaxonomy(db, user?.organizationId);
 * 
 * @param {Firestore} db - Firestore instance
 * @param {string} organizationId - The organization ID
 * @returns {{ taxonomy: string[], loading: boolean, error: Error|null }}
 */
export function useIndustryTaxonomy(db, organizationId) {
  const [taxonomy, setTaxonomy] = useState(DEFAULT_INDUSTRY_TAXONOMY);  // Display labels for UI
  const [rawTaxonomy, setRawTaxonomy] = useState(null);  // Original object array for canonical mapping
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allowOther, setAllowOther] = useState(true);  // Firms can disable "Other" option

  useEffect(() => {
    let mounted = true;

    const fetchTaxonomy = async () => {
      setLoading(true);
      setError(null);
      
      // Individual FCFOs (no org) get default taxonomy with "Other" allowed
      if (!organizationId || !db) {
        if (mounted) {
          setTaxonomy(DEFAULT_INDUSTRY_TAXONOMY);
          setRawTaxonomy(null);
          setAllowOther(true);
          setLoading(false);
        }
        return;
      }
      
      try {
        const orgRef = doc(db, 'organizations', organizationId);
        const orgSnap = await getDoc(orgRef);

        if (mounted && orgSnap.exists()) {
          const orgData = orgSnap.data();
          
          if (orgData.industryTaxonomy && Array.isArray(orgData.industryTaxonomy) && orgData.industryTaxonomy.length > 0) {
            const orgTaxonomy = orgData.industryTaxonomy;
            
            // Check if new structure (objects with canonical/displayLabel) or legacy (strings)
            if (typeof orgTaxonomy[0] === 'object' && orgTaxonomy[0].canonical) {
              // New structure: store raw for mapping, extract labels for UI
              setRawTaxonomy(orgTaxonomy);
              const displayLabels = orgTaxonomy.map(item => item.displayLabel || item.canonical);
              // Ensure "Other" is available if org allows it
              if (orgData.allowOtherIndustry !== false && !displayLabels.includes('Other')) {
                displayLabels.push('Other');
              }
              setTaxonomy(displayLabels);
            } else {
              // Legacy string array - no mapping needed
              setRawTaxonomy(null);
              const legacyTaxonomy = [...orgTaxonomy];
              if (!legacyTaxonomy.includes('Other')) {
                legacyTaxonomy.push('Other');
              }
              setTaxonomy(legacyTaxonomy);
            }
          } else {
            setTaxonomy(DEFAULT_INDUSTRY_TAXONOMY);
            setRawTaxonomy(null);
          }
          
          // Firms can disable "Other" to enforce taxonomy discipline
          setAllowOther(orgData.allowOtherIndustry !== false);
        } else if (mounted) {
          setTaxonomy(DEFAULT_INDUSTRY_TAXONOMY);
          setRawTaxonomy(null);
          setAllowOther(true);
        }
      } catch (err) {
        console.error('[useIndustryTaxonomy] Fetch failed:', err);
        if (mounted) {
          setError(err);
          setRawTaxonomy(null);
          setAllowOther(true);
          // Keep default taxonomy on error
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchTaxonomy();

    return () => {
      mounted = false;
    };
  }, [db, organizationId]);

  return { taxonomy, rawTaxonomy, loading, error, allowOther };
}

/**
 * Maps a display label back to its canonical value for storage
 * Used when saving to Firestore - we always save the canonical value
 * 
 * @param {string} displayValue - The display label shown in UI
 * @param {Array} orgTaxonomy - The org's taxonomy array (may be objects or strings)
 * @returns {string} - The canonical value to save
 */
export const getCanonicalFromDisplay = (displayValue, orgTaxonomy) => {
  if (!displayValue || !orgTaxonomy || orgTaxonomy.length === 0) {
    return displayValue;  // Return as-is if no mapping available
  }
  
  // Check if taxonomy uses new { canonical, displayLabel } structure
  if (typeof orgTaxonomy[0] === 'object' && orgTaxonomy[0].canonical) {
    const match = orgTaxonomy.find(
      item => item.displayLabel === displayValue || item.canonical === displayValue
    );
    return match ? match.canonical : displayValue;
  }
  
  // Legacy string array - displayValue IS the canonical value
  return displayValue;
};

/**
 * Validates an industry value against the taxonomy
 * Returns the value if valid, or null if not found
 * 
 * @param {string} industry - The industry to validate
 * @param {string[]} taxonomy - The taxonomy array to validate against
 * @returns {string|null}
 */
export function validateIndustry(industry, taxonomy) {
  if (!industry || !taxonomy) return null;
  
  // Case-insensitive match
  const normalized = industry.trim().toLowerCase();
  const match = taxonomy.find(t => t.toLowerCase() === normalized);
  
  return match || null;
}

/**
 * Gets a display-friendly industry name
 * Handles edge cases like null, undefined, empty string
 * 
 * @param {string} industry - The industry value
 * @returns {string}
 */
export function getIndustryDisplayName(industry) {
  if (!industry || typeof industry !== 'string') {
    return 'Not specified';
  }
  return industry.trim() || 'Not specified';
}
